// ===========================================
// TIPOS: Documentos (CORE)
// ===========================================

import type { Tables, TablesInsert, TablesUpdate } from '@/types/database'

// Re-export tipos base desde database.ts
export type {
  Documento,
  DocumentoConCliente,
  CategoriaDocumento,
} from '@/types/database'

// Tipos derivados de Supabase (generados automáticamente)
export type DocumentoRow = Tables<'documentos'>
export type DocumentoInsert = TablesInsert<'documentos'>
export type DocumentoUpdate = TablesUpdate<'documentos'>

export type DocumentoVinculacionRow = Tables<'documento_vinculaciones'>
export type DocumentoVinculacionInsert = TablesInsert<'documento_vinculaciones'>
export type DocumentoVinculacionUpdate = TablesUpdate<'documento_vinculaciones'>

// ===========================================
// Tipos de vinculación polimórfica
// ===========================================

export type TipoEntidadDocumento =
  | 'cliente'
  | 'ddjj'
  | 'expediente'
  | 'empleado'
  | 'vencimiento'
  | 'tarea'
  | 'persona'
  | 'movimiento'
  | 'otro'

export type RolDocumento =
  | 'adjunto'
  | 'comprobante'
  | 'contrato'
  | 'poder'
  | 'identificacion'
  | 'respaldo'
  | 'otro'

// Alias para compatibilidad con código existente
export type DocumentoVinculacion = DocumentoVinculacionRow

// ===========================================
// Filtros y formularios
// ===========================================

export interface FiltrosDocumentos {
  cliente_id?: string
  categoria?: string
  busqueda?: string
  workspace?: string
  entidad_tipo?: TipoEntidadDocumento
  entidad_id?: string
}

export interface MetadatosSubida {
  nombre?: string
  categoria?: string
  fecha_vencimiento?: string
  workspace?: string
  descripcion?: string
}

// ===========================================
// Estadísticas
// ===========================================

export interface EstadisticasDocumentos {
  total: number
  porCategoria: Record<string, number>
  tamanioTotal: number
}

export interface CategoriaStats {
  categoria: string
  cantidad: number
  tamanoTotal: number
}

export interface EstadisticasDetalladas {
  total: number
  tamanioTotal: number
  categorias: CategoriaStats[]
}

// ===========================================
// Configuración de categorías para UI
// ===========================================

// Categorías para el workspace CONTABLE (usadas en formularios y carpetas)
export const CATEGORIAS_CONTABLE = [
  'poderes',
  'balances',
  'ddjj',
  'acuse', // ORR-191: adjuntos de presentación fiscal (calendario)
  'vep',   // ORR-191
  'contratos',
  'constancias',
  'identificacion',
  'comprobantes',
  'otros',
] as const

export type CategoriaContable = (typeof CATEGORIAS_CONTABLE)[number]

// Configuración visual de todas las categorías (incluye jurídicas para futuro)
export const CATEGORIAS_DOCUMENTO: Record<string, { label: string; color: string }> = {
  poderes: { label: 'Poderes', color: 'bg-purple-100 text-purple-800' },
  balances: { label: 'Balances', color: 'bg-blue-100 text-blue-800' },
  ddjj: { label: 'DDJJ', color: 'bg-green-100 text-green-800' },
  contratos: { label: 'Contratos', color: 'bg-orange-100 text-orange-800' },
  constancias: { label: 'Constancias', color: 'bg-cyan-100 text-cyan-800' },
  identificacion: { label: 'Identificación', color: 'bg-pink-100 text-pink-800' },
  comprobantes: { label: 'Comprobantes de Pago', color: 'bg-accent text-accent-foreground' },
  otros: { label: 'Otros', color: 'bg-gray-100 text-gray-800' },
  // Categorías de presentación fiscal
  acuse: { label: 'Acuse', color: 'bg-teal-100 text-teal-800' },
  vep: { label: 'VEP', color: 'bg-yellow-100 text-yellow-800' },
  // Alias para compatibilidad con datos legacy que usan "otro" (singular)
  otro: { label: 'Otro', color: 'bg-gray-100 text-gray-800' },
  // Categorías jurídicas (futuro workspace jurídico)
  demandas: { label: 'Demandas', color: 'bg-red-100 text-red-800' },
  escrituras: { label: 'Escrituras', color: 'bg-amber-100 text-amber-800' },
  sentencias: { label: 'Sentencias', color: 'bg-indigo-100 text-indigo-800' },
}

// Categorías para adjuntos de presentación fiscal (calendario)
export const CATEGORIAS_PRESENTACION = [
  'ddjj',
  'acuse',
  'vep',
  'otros',
] as const

export type CategoriaPresentacion = (typeof CATEGORIAS_PRESENTACION)[number]

// Helper para obtener opciones de categorías por workspace
export function getCategoriasOptions(workspace: 'contable' | 'juridico' = 'contable') {
  const categorias = workspace === 'contable'
    ? CATEGORIAS_CONTABLE
    : ['demandas', 'escrituras', 'sentencias', 'contratos', 'poderes', 'otros'] as const

  return categorias.map((value) => ({
    value,
    label: CATEGORIAS_DOCUMENTO[value]?.label || value,
  }))
}

export const ROL_DOCUMENTO_CONFIG: Record<RolDocumento, { label: string; descripcion: string }> = {
  adjunto: { label: 'Adjunto', descripcion: 'Documento adjunto general' },
  comprobante: { label: 'Comprobante', descripcion: 'Comprobante de presentación o pago' },
  contrato: { label: 'Contrato', descripcion: 'Contrato o acuerdo' },
  poder: { label: 'Poder', descripcion: 'Poder legal' },
  identificacion: { label: 'Identificación', descripcion: 'Documento de identidad' },
  respaldo: { label: 'Respaldo', descripcion: 'Documento de respaldo' },
  otro: { label: 'Otro', descripcion: 'Otro tipo de documento' },
}

// ===========================================
// Constantes de validación
// ===========================================

export const MIME_TYPES_PERMITIDOS = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

export const EXTENSIONES_PERMITIDAS = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.doc', '.docx', '.xls', '.xlsx']

export const LIMITE_ARCHIVO_MB = 10
export const LIMITE_ARCHIVO_BYTES = LIMITE_ARCHIVO_MB * 1024 * 1024
export const LIMITE_CLIENTE_MB = 100

// ===========================================
// Helpers
// ===========================================

export function getIconoMimeType(mimeType: string | null): string {
  if (!mimeType) return 'file'
  if (mimeType.startsWith('image/')) return 'image'
  if (mimeType === 'application/pdf') return 'file-text'
  if (mimeType.includes('word')) return 'file-type'
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'table'
  return 'file'
}

export function formatFileSize(bytes: number | null): string {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  let unitIndex = 0
  let size = bytes
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`
}

export function esArchivoPermitido(file: File): boolean {
  const extension = '.' + file.name.split('.').pop()?.toLowerCase()
  return EXTENSIONES_PERMITIDAS.includes(extension) || MIME_TYPES_PERMITIDOS.includes(file.type)
}

export function esTamanoPermitido(file: File): boolean {
  return file.size <= LIMITE_ARCHIVO_BYTES
}
